import React, { useEffect, useMemo } from 'react';
import { Button, Spin, Tooltip } from 'antd';
import { AWAssetDto } from '@api/Api';
import type { StepItemKey } from '@components/Modals/ApprovalModal/ApprovalModal/components/Steps/Steps';
import { useCampaignQuery } from '@hooks/queries/campaigns';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useAssetsQuery } from '@hooks/queries/assets';
import AllAssets from './components/AllAssets';
import AssetsPreview from './components/AssetsPreview';
import './MediaStep.less';

type MediaStepProps = {
  campaignId: string;
  approvalRoutingName: string;
  approvalWorkflowAssets?: AWAssetDto[];
  selectedAssetVersions: AWAssetDto[];
  setSelectedAssetVersions: (value: AWAssetDto[]) => void;
  onChangeStep: (value: StepItemKey) => void;
  onCloseModal: () => void;
};

export default function MediaStep({
  campaignId,
  approvalRoutingName,
  approvalWorkflowAssets,
  selectedAssetVersions,
  onChangeStep,
  setSelectedAssetVersions,
  onCloseModal
}: MediaStepProps) {
  const [currentWorkspace] = useCurrentWorkspace();
  const { data: campaign } = useCampaignQuery({
    campaignId
  });

  const companyName = campaign?.name || '';

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isLoading } =
    useAssetsQuery({
      workspaceId: currentWorkspace.id,
      campaignId,
      flatten: true,
      limit: 100
    });

  const loading = isLoading || isFetchingNextPage;

  const assetList = useMemo(() => {
    return (
      data?.pages.flatMap((page) => page.edges.map((edge) => edge.node)) ?? []
    );
  }, [data]);

  useEffect(() => {
    if (isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const goNextStep = () => {
    onChangeStep('stages');
  };

  return (
    <div className="approval-step media-step">
      <div className="approval-step_head">
        <h2 className="approval-step_title">
          Media
          <Tooltip
            title={`${approvalRoutingName} approval routing`}
            placement="topLeft"
            overlayClassName="approval-modal-tooltip"
            className="approval-step_route-name"
          >
            {approvalRoutingName} approval routing
          </Tooltip>
        </h2>
        <div className="approval-step_text">
          Choose media to route for approval, or streamline the process by
          selecting all media within a project or folder.
        </div>
      </div>
      <div className="approval-step_wrapper media-step_wrapper">
        {loading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%'
            }}
          >
            <Spin size="large" />
          </div>
        )}
        {!loading && (
          <>
            <AllAssets
              companyName={companyName}
              assetList={assetList}
              approvalWorkflowAssets={approvalWorkflowAssets}
              selectedAssetVersions={selectedAssetVersions}
              setSelectedAssetVersions={setSelectedAssetVersions}
            />
            <AssetsPreview
              companyName={companyName}
              selectedAssetVersions={selectedAssetVersions}
              setSelectedAssetVersions={setSelectedAssetVersions}
            />
          </>
        )}
      </div>
      <div className="approval-step_buttons">
        <Button type="text" onClick={onCloseModal}>
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={!selectedAssetVersions.length}
          onClick={goNextStep}
          style={{
            minWidth: 152
          }}
        >
          Next step
        </Button>
      </div>
    </div>
  );
}
